import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import {
	Box,
	List,
	ListItem,
	Theme,
	Button,
	ListItemText,
	Switch,
	Typography,
} from '@mui/material';
import { createStyles, useTheme } from '@mui/styles';
import makeStyles from '@mui/styles/makeStyles';
import ManualPaymentMethodRow from './ManualPaymentMethodRow';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as NotificationActions from 'actions/NotificationActions';
import AddManualPaymentMethodDialog from './AddManualPaymentMethodDialog';
import {
	ManualPaymentMethodDetails,
	ShopOnlinePaymentMethodObject,
} from 'common/modules/payments/types';
import { useTranslation } from 'services/localization';
import {
	isCustomShopPaymentMethodObject,
	shopHasPaymentMethod,
} from 'common/modules/payments/paymentMethods';
import { api } from 'common/frontend/api';
import BackdropDialog from 'common/components/BackdropDialog';
import HasFeature from 'components/features/HasFeature';
import UpgradeButton from 'components/features/UpgradeButton';
import * as ViewActions from 'actions/ViewActions';
import { AddOnChannels } from 'common/modules/plans';

const ManualPaymentMethods = () => {
	const theme = useTheme();
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const location = useLocation();
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const manualPaymentMethods = useSelector(ShopSelectors.getManualPaymentMethods);
	const activePaymentMethods = useSelector(ShopSelectors.getActivePaymentMethods);
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
	const [manualPaymentMethodId, setManualPaymentMethodId] = useState<string | null>(null);
	const [
		editablePaymentMethod,
		setEditablePaymentMethod,
	] = useState<ManualPaymentMethodDetails | null>(null);

	const classes = useStyles();

	const displaySaveError = () =>
		dispatch(
			NotificationActions.showNotification({
				message: t('common:errors.somethingWentWrong'),
				variant: 'error',
			}),
		);

	const handlePaymentMethodCreate = async (method: ManualPaymentMethodDetails) => {
		try {
			await api()
				.shops.doc(shopId)
				.update({
					manualPaymentMethods: [...manualPaymentMethods, method],
				});
		} catch (err) {
			displaySaveError();
		}
	};

	const handlePaymentMethodEdit = async (method: ManualPaymentMethodDetails) => {
		const updatedManualPaymentMethods = manualPaymentMethods.map((m) =>
			m.id === method.id ? method : m,
		);
		const updatedActivePaymentMethods = activePaymentMethods.map((m) =>
			!isCustomShopPaymentMethodObject(m)
				? m
				: m.details.id === method.id
				? { ...m, details: method }
				: m,
		);
		try {
			await Promise.all([
				api().shops.doc(shopId).public.update({
					activePaymentMethods: updatedActivePaymentMethods,
				}),
				api().shops.doc(shopId).update({
					manualPaymentMethods: updatedManualPaymentMethods,
				}),
			]);
		} catch (err) {
			displaySaveError();
		}
	};

	const handlePaymentMethodToggle = async (id: string, enabled: boolean) => {
		const paymentMethod = manualPaymentMethods.find((method) => method.id === id);
		if (!paymentMethod) return;

		if (enabled) {
			try {
				const updatedActivePaymentMethods: ShopOnlinePaymentMethodObject[] = [
					...activePaymentMethods,
					{ id: 'MANUAL', details: paymentMethod },
				];

				await api().shops.doc(shopId).public.update({
					activePaymentMethods: updatedActivePaymentMethods,
				});
			} catch (err) {
				displaySaveError();
			}
		} else {
			try {
				const updatedActivePaymentMethods = activePaymentMethods.filter((method) =>
					!isCustomShopPaymentMethodObject(method) ? true : method.details.id !== paymentMethod.id,
				);
				await api().shops.doc(shopId).public.update({
					activePaymentMethods: updatedActivePaymentMethods,
				});
			} catch (err) {
				displaySaveError();
			}
		}
	};

	const handlePayInStoreMethodChange = async (
		e: React.ChangeEvent<HTMLInputElement>,
		enabled: boolean,
	) => {
		const payStoreObject: ShopOnlinePaymentMethodObject = { id: 'PAY_STORE' };
		if (enabled) {
			try {
				const updatedActivePaymentMethods = [...activePaymentMethods, payStoreObject];

				await api().shops.doc(shopId).public.update({
					activePaymentMethods: updatedActivePaymentMethods,
				});
			} catch (err) {
				displaySaveError();
			}
		} else {
			try {
				const updatedActivePaymentMethods = activePaymentMethods.filter(
					(m) => m.id !== payStoreObject.id,
				);

				await api().shops.doc(shopId).public.update({
					activePaymentMethods: updatedActivePaymentMethods,
				});
			} catch (err) {
				displaySaveError();
			}
		}
	};

	const openManualPaymentMethodDialog = (id?: string, edit?: boolean) => {
		if (!!edit) {
			const paymentMethod = manualPaymentMethods.find((method) => method.id === id);
			if (!paymentMethod) return;

			setEditablePaymentMethod(paymentMethod);
			setIsOpen(true);
		} else {
			setEditablePaymentMethod(null);
			setIsOpen(true);
		}
	};

	const deleteManualPaymentMethod = async () => {
		const method = manualPaymentMethods.find((method) => method.id === manualPaymentMethodId);

		if (!method) return;

		const updatedManualPaymentMethods = manualPaymentMethods.filter(
			(method) => method.id !== manualPaymentMethodId,
		);

		const updatedActivePaymentMethods = activePaymentMethods.filter((method) =>
			!isCustomShopPaymentMethodObject(method) ? true : method.details.id !== manualPaymentMethodId,
		);

		try {
			await Promise.all([
				api().shops.doc(shopId).public.update({
					activePaymentMethods: updatedActivePaymentMethods,
				}),
				api().shops.doc(shopId).update({
					manualPaymentMethods: updatedManualPaymentMethods,
				}),
			]);
		} catch (err) {
			displaySaveError();
		}

		setIsDeleteModalOpen(false);
	};

	const handleDelete = (id: string) => {
		setManualPaymentMethodId(id);
		setIsDeleteModalOpen(true);
	};

	return (
		<>
			<Box>
				<List>
					<ListItem divider className={classes.listItem}>
						<ListItemText
							classes={{
								root: classes.listItemText,
							}}
							inset
							sx={{ paddingLeft: 0 }}
							primary={
								<Box sx={{ fontWeight: 400 }}>
									<Switch
										checked={shopHasPaymentMethod('PAY_STORE', activePaymentMethods)}
										onChange={handlePayInStoreMethodChange}
									/>
									{t('shop.paymentMethodDescription.PAY_STORE')}
								</Box>
							}
						/>
					</ListItem>
					{manualPaymentMethods.map((method) => (
						<ManualPaymentMethodRow
							paymentMethodObject={method}
							onDelete={handleDelete}
							onToggle={handlePaymentMethodToggle}
							onEdit={openManualPaymentMethodDialog}
							key={method.id}
						/>
					))}
				</List>

				<HasFeature
					requiredFeature="MANUAL_PAYMENT_METHODS"
					fallback={
						<UpgradeButton
							text={t(
								'account.payments.enableCustomManualPayments',
								'Enable Custom Manual Payment Methods',
							)}
							onClick={() =>
								dispatch(
									ViewActions.openAddOnModal(
										'MANUAL_PAYMENT_METHODS',
										AddOnChannels.cta_button,
										location.pathname,
									),
								)
							}
						/>
					}
				>
					<Button
						sx={{ marginTop: theme.spacing(2) }}
						variant="outlined"
						color="primary"
						onClick={() => openManualPaymentMethodDialog()}
					>
						{t('account.payments.addMethod', 'Add payment method')}
					</Button>
				</HasFeature>
			</Box>
			{isOpen && (
				<AddManualPaymentMethodDialog
					isOpen={isOpen}
					onClose={setIsOpen}
					onCreate={handlePaymentMethodCreate}
					onEdit={handlePaymentMethodEdit}
					paymentMethod={editablePaymentMethod ?? undefined}
				/>
			)}
			<BackdropDialog
				isOpen={isDeleteModalOpen}
				onClose={() => setIsDeleteModalOpen(false)}
				label={t('account.payments.deleteMethod', 'Delete payment method')}
				body={
					<Box>
						<Typography variant="body2">
							{t(
								'account.payments.deleteModalContent2',
								`After deleting, the payment method will no longer be available. Already received orders won't be changed.`,
							)}
						</Typography>
						<Box mb={2} />
						<Typography variant="body2">{t('common:warnings.cannotBeUndone')}</Typography>
					</Box>
				}
				variant="danger"
				width="sm"
				confirm={{
					label: t('common:actions.delete'),
					callback: deleteManualPaymentMethod,
					closeAfterDone: true,
				}}
				cancel={{
					label: t('common:actions.cancel'),
				}}
			/>
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		listItem: {
			padding: theme.spacing(2, 2, 2, 0),
		},
		listItemText: {
			minWidth: '20%',
			[theme.breakpoints.down('sm')]: {
				paddingRight: 0,
			},
		},
		listItemTextInset: {
			paddingLeft: 0,
			'&:first-child': {
				paddingLeft: '12px',
			},
		},
	}),
);

export default ManualPaymentMethods;
